import React, { useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import GitHubContributions from '../GitHubContributions/GitHubContributions';
import AnimeJsIcon from "../../Assets/anime-js-logo-v4.svg";
import SnowflakeIcon from "../../Assets/Snowflake-logo.svg";
import PowerBiIcon from "../../Assets/Power-BI-logo.svg";
import OracleIcon from "../../Assets/Oracle-Logo.svg";
import { ReactComponent as IsometricSVG } from "../../Assets/Isometric_SVG_2.svg";
import SkillCard from '../SkillCard/SkillCard';
import './About.css';

const About = () => {
  const aboutRef = useRef(null);
  
  useEffect(() => {
    const node = aboutRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          entry.target.classList.add("visible");
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
      observer.disconnect();
    }
  }, []);
  
  const professionalSkills = [
    // Data Engineering Skills
    {name: "Python", icon: "devicon-python-plain"},
    {name: "SQL", icon: "devicon-mysql-original"},
    {name: "PySpark", icon: "devicon-apachespark-original"},
    {name: "Kafka", icon: "devicon-apachekafka-original"},
    {name: "Airflow", icon: "devicon-apacheairflow-plain"},
    {name: "PostgreSQL", icon: "devicon-postgresql-plain"},
    {name: "Snowflake", svg_icon: SnowflakeIcon},
    {name: "Power BI", svg_icon: PowerBiIcon},

    // Backend / Core
    {name: "Java", icon: "devicon-java-plain"},
    {name: "Node.js", icon: "fa-brands fa-node-js"},
    {name: "Express", icon: "devicon-express-original"},

    // Frontend
    {name: "JavaScript", icon: "devicon-javascript-plain"},
    {name: "TypeScript", icon: "devicon-typescript-plain"},
    {name: "React", icon: "devicon-react-original"},
    {name: "Next.js", icon: "devicon-nextjs-plain"},
    {name: "HTML5", icon: "devicon-html5-plain"},
    {name: "CSS3", icon: "devicon-css3-plain"},
    {name: "Bootstrap", icon: "devicon-bootstrap-plain"},

    // Databases
    {name: "MySQL", icon: "devicon-mysql-plain"},
    {name: "Oracle SQL", svg_icon: OracleIcon, customClass: "oracle-icon"}, // need to get svg
    {name: "Azure SQL Database", icon: "devicon-azuresqldatabase-plain"},
    {name: "Firebase / Firestore", icon: "devicon-firebase-plain"},
    {name: "Anime.js", svg_icon: AnimeJsIcon, customClass: "anime-icon"},

  ];
  
  const tools = [
    { name: "Windows", icon: "devicon-windows8-original" },
    { name: "VS Code", icon: "devicon-vscode-plain" },
    { name: "Git", icon: "devicon-git-plain" },
    { name: "GitHub", icon: "devicon-github-plain"},
  ];
  
  return (
    <section className="about-section" id="about">
      <Container>
        <div ref={aboutRef} className="about-container">
          <Row className="about-content">
            <Col md={6} className="about-column text-center">
              <h2 className="section-heading">Who I Am</h2>
              <div className="about-text">
                <p>
                  Hello! I'm Brandon Mihalko, a Software Engineer with a B.S. in Integrated Information Technology from the University of South Carolina and currently pursuing a M.S. in Software Engineering. I specialize in building data pipelines and backend systems, with experience in Python, SQL, and distributed data processing
                </p>
                <p>
                  Recently, I've focused on developing data-driven applications, including building an end-to-end pipeline that ingests, transforms, and visualizes data. My work spans API integration, database design, and analytics workflows, and I enjoy creating systems that turn raw data into meaningful insights. I value clean, maintainable code and enjoy working in environments that emphasize collaboration and continuous learning.
                </p>
              </div>
            </Col>
            
            <Col md={6} className="about-svg-container d-flex justify-content-center align-items-center">
              <div className="isometric-svg-wrapper">
                <IsometricSVG className="isometric-svg" />
              </div>
            </Col>
          </Row>
          
          <Row className="mt-5">
            <Col md={12} className="text-center">
              <h2 className="skillset-heading">My <span className="highlight">Skillset</span></h2>
              <div className="skills-grid">
                {professionalSkills.map((skill, index) => (
                  <SkillCard 
                  key={index} 
                  name={skill.name} 
                  icon={skill.icon} 
                  svg_icon={skill.svg_icon}
                  customClass={skill.customClass}
                />
                ))}
              </div>
            </Col>
          </Row>
          
          <Row className="mt-5">
            <Col md={12} className="text-center">
              <h2 className="tools-heading">Tools <span className="highlight">I use</span></h2>
              <div className="tools-grid">
                {tools.map((tool, index) => (
                  <SkillCard 
                    key={index} 
                    name={tool.name} 
                    icon={tool.icon} 
                    svg_icon={tool.svg_icon} 
                  />
                ))}
              </div>
            </Col>
          </Row>
          
          <Row className="mt-5">
            <Col md={12}>
              <GitHubContributions username = "BMIHALKO" />
            </Col>
          </Row>
        </div>
      </Container>
    </section>
  );
};

export default About;